import { useMutation, useQuery } from '@tanstack/react-query';
import { getPrivateAxiosInstance } from '../../../axiosInstances/axiosPrivate';
import { AppointmentRequest } from '../../../types/appointmentRequests';
import {
  AppointmentSlot,
  AvailableAssociateRes,
} from '../../../types/appointments';
import { AcademiaResponse } from '../../../types/common';
import { returnQueryConfig } from '../../../utils/commonFunctions';

const useAppointmentActions = (
  whichAction: '',
  associateId?: number,
  clientId?: number,
  appointmentType?: string
) => {
  //query function to fetch available slots of associate from database declared here
  const getAvailableSlotsHandler = useMutation({
    mutationFn: async ({
      body,
    }: {
      body: {
        associate_id: number;
        service_id: number;
        date: string;
        current_time: string;
      };
    }): Promise<AcademiaResponse<Array<AppointmentSlot>>> => {
      const response = await getPrivateAxiosInstance('appointment').post(
        '/appointment/available/slots/list/',
        body
      );

      return response.data;
    },
  });

  // mutation function to fetch available associates by searching declared here
  const getAvailableAssociatesHandler = useMutation({
    mutationFn: async ({
      body,
    }: {
      body: {
        service_id: number;
        selected_date: string;
        slot_start_time?: string;
        slot_end_time?: string;
      };
    }): Promise<AcademiaResponse<AvailableAssociateRes>> => {
      const response = await getPrivateAxiosInstance('appointment').post(
        '/filter/available/associates/',
        {
          ...body,
          current_time: new Date().toLocaleTimeString('en-GB', {
            hour12: false,
          }),
        }
      );

      return response.data;
    },
  });

  // query function to fetch appointments by associate id declared here
  const getAppointmentsByAssociateIdHandler = useQuery({
    queryKey: ['appointment-by-associate-id', associateId, appointmentType],
    queryFn: async (): Promise<AcademiaResponse<Array<AppointmentRequest>>> => {
      const response = await getPrivateAxiosInstance('appointment').get(
        `/associate/${associateId}/appointments/${appointmentType}/`
      );

      return response.data;
    },

    ...returnQueryConfig(!!associateId && !!appointmentType),
  });

  const getAppointmentsByClientIdHandler = useQuery({
    queryKey: ['appointment-by-client-id', clientId, appointmentType],
    queryFn: async (): Promise<AcademiaResponse<Array<AppointmentRequest>>> => {
      const response = await getPrivateAxiosInstance('appointment').get(
        `/client/${clientId}/appointments/${appointmentType}/`
      );

      return response.data;
    },

    ...returnQueryConfig(!!clientId && !!appointmentType),
  });

  return {
    getAvailableSlotsHandler,
    getAvailableAssociatesHandler,
    getAppointmentsByAssociateIdHandler,
    getAppointmentsByClientIdHandler,
  };
};

export default useAppointmentActions;
